import { FC } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { plainDateToZonedDateTime } from '../../../util/date-utilts';
import { calendarShortcutToValueMap, shortcutsSx } from '../../DatePicker/Calendar';
import { IDateMeta } from '../../../data-models/field3.data-model';
import { formTextFieldInlineClassNames } from '../../../theme/component-styles';
import { IBaseFieldProps } from './types';

/**
 * The MUI date picker has a limit of 100 years in the past / future by default, see:
 * https://github.com/mui/mui-x/issues/7072
 */
export const FieldDate: FC<IBaseFieldProps<IDateMeta>> = ({ formField, formProps }) => {
  const { ref, onChange, onBlur, value } = formProps;
  const { disabled = false, fullWidth = false, variant, autoFocus = false } = formField ?? {};
  const { minimum, maximum, presets, gridEditor = false } = formField.rendererMeta ?? {};
  const className = variant === 'form-inline' ? `${formTextFieldInlineClassNames} form-date-picker` : '';
  const shortcutItems = presets?.map((preset) => ({
    label: preset,
    getValue: calendarShortcutToValueMap[preset],
  }));

  let dateValue = value;
  if (typeof value === 'string') {
    // '' means no date. Converted, it becomes an Invalid Date, which the picker paints red.
    dateValue = value ? plainDateToZonedDateTime(value) : null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        defaultValue={dateValue}
        disabled={disabled}
        maxDate={maximum ? new Date(maximum) : undefined}
        minDate={minimum ? new Date(minimum) : undefined}
        onAccept={onChange}
        onChange={onChange}
        onClose={onBlur}
        views={['year', 'month', 'day']}
        slotProps={{
          shortcuts: {
            items: shortcutItems,
            sx: shortcutsSx,
          },
          textField: {
            className,
            onBlur: gridEditor ? undefined : onBlur,
            ref,
            placeholder: variant === 'form-inline' ? '' : undefined,
            fullWidth,
            // As in `FieldText`: the shared label carries no `htmlFor`, so without this the
            // picker's input has no accessible name.
            inputProps: { 'aria-label': formField.label },
          },
        }}
        autoFocus={autoFocus}
      />
    </LocalizationProvider>
  );
};
